// mchataiSourceTemplatesAPI.js — read-only enumeration of templates currently
// living in mchatai-source/harness/templates/ via the public GitHub APIs.
//
// Used by:
//   - TemplateCandidatesAdminPage  → collision detection in the Promote form
//   - TemplatesCatalogAdminPage    → /admin/templates list view
//
// Why GitHub APIs instead of a Cloud Function: these reads are public-repo,
// CORS-friendly, and unauthenticated (60 req/h limit per IP — fine for a
// rare-load admin page). Using a Cloud Function would add a deploy step + a
// secret to maintain for no real benefit.
//
// Two-phase fetch:
//   1. GET /git/trees/main?recursive=1 — returns the entire repo's path
//      list in one request. Filter to manifest paths.
//   2. For each manifest path, GET the raw URL to read its JSON content.
//
// Reference HTML is NOT fetched here — it's loaded on demand via
// referenceURL() (a jsdelivr CDN URL) so the list view stays light.

const REPO_OWNER = 'LawrenceNorman';
const REPO_NAME = 'mchatai-source';
const REPO_BRANCH = 'main';
const TEMPLATES_BASE = 'harness/templates';

/// Returns a Promise<Array<{ slug, subdir, manifest, referenceURL, manifestURL, githubURL }>>
/// where manifest is the parsed Phase J template-manifest.json.
export async function fetchPromotedTemplates() {
  const treeResp = await fetch(
    `https://api.github.com/repos/${REPO_OWNER}/${REPO_NAME}/git/trees/${REPO_BRANCH}?recursive=1`,
    { headers: { 'Accept': 'application/vnd.github+json' } }
  );
  if (!treeResp.ok) throw new Error(`GitHub tree API ${treeResp.status}`);
  const tree = await treeResp.json();
  if (tree.truncated) {
    console.warn('[mchataiSourceTemplatesAPI] repo tree truncated — some templates may be missing from the list.');
  }

  // Filter to template-manifest.json paths under harness/templates/<subdir>/<slug>/.
  const manifestPaths = (tree.tree || [])
    .filter(item => item.type === 'blob' &&
      item.path.startsWith(`${TEMPLATES_BASE}/`) &&
      item.path.endsWith('/template-manifest.json'))
    .map(item => item.path);

  const results = await Promise.all(manifestPaths.map(async (path) => {
    // path is e.g. "harness/templates/miniapps/connect-four-board-game/template-manifest.json"
    const parts = path.split('/');
    const subdir = parts[2];
    const slug = parts[3];
    const manifestURL = `https://raw.githubusercontent.com/${REPO_OWNER}/${REPO_NAME}/${REPO_BRANCH}/${path}`;
    let manifest = null;
    try {
      const r = await fetch(manifestURL);
      if (r.ok) manifest = await r.json();
    } catch (err) {
      console.warn(`[mchataiSourceTemplatesAPI] failed to load manifest ${path}:`, err.message);
    }
    return {
      slug, subdir,
      manifest,
      referenceURL: `https://cdn.jsdelivr.net/gh/${REPO_OWNER}/${REPO_NAME}@${REPO_BRANCH}/${TEMPLATES_BASE}/${subdir}/${slug}/reference.html`,
      manifestURL,
      githubURL: `https://github.com/${REPO_OWNER}/${REPO_NAME}/tree/${REPO_BRANCH}/${TEMPLATES_BASE}/${subdir}/${slug}`
    };
  }));

  // Sort by subdir + slug for stable list ordering.
  results.sort((a, b) =>
    a.subdir.localeCompare(b.subdir) || a.slug.localeCompare(b.slug)
  );
  return results;
}

/// Build a {slug → {subdir, manifest, ...}} map from the array. Convenience
/// for collision-detection use cases (slug → exists?).
export function indexBySlug(templates) {
  const out = {};
  for (const t of templates) out[t.slug] = t;
  return out;
}
